import type { LocalDataColumn } from "@nymbl/nos2-rules";
// src/lib/team/details.ts
import { TEAM_HELP, TEAM_SUMMARY_HELP } from "@/lib/column-help";
import type { TeamTagFilter } from "@/lib/enums";
import { formatHours, formatPct, type MetricValues, type PersonMetrics } from "@/lib/team/metrics";

/** The My Team Details table (DECISIONS 2026-10-15): four groups, twelve numbers. */

type DetailKey = keyof MetricValues;
type DetailGroup = "Availability" | "Guidance" | "Billable target" | "Logged to date";
/** Which person-panel tab a number opens. */
export type Drill = "guidance" | "billable" | "entries";

export interface DetailColumn {
  key: DetailKey;
  label: string;
  csvLabel: string;
  group: DetailGroup;
  help: string;
  kind: "hours" | "pct";
  drill?: Drill;
}

export const DETAIL_COLUMNS: readonly DetailColumn[] = [
  {
    key: "capacity",
    label: "Capacity",
    csvLabel: "Capacity",
    group: "Availability",
    kind: "hours",
    help: TEAM_HELP.capacity,
  },
  {
    key: "pto",
    label: "PTO",
    csvLabel: "PTO",
    group: "Availability",
    kind: "hours",
    help: TEAM_HELP.pto,
  },
  {
    key: "available",
    label: "Available",
    csvLabel: "Available",
    group: "Availability",
    kind: "hours",
    help: TEAM_HELP.available,
  },
  {
    key: "planned",
    label: "Total",
    csvLabel: "Guidance total",
    group: "Guidance",
    kind: "hours",
    help: TEAM_HELP.planned,
    drill: "guidance",
  },
  {
    key: "plannedBillable",
    label: "Billable",
    csvLabel: "Guidance billable",
    group: "Guidance",
    kind: "hours",
    help: TEAM_HELP.plannedBillable,
  },
  {
    key: "plannedPct",
    label: "% of avail.",
    csvLabel: "Guidance % of available",
    group: "Guidance",
    kind: "pct",
    help: TEAM_HELP.plannedPct,
  },
  {
    key: "targetHours",
    label: "Hours",
    csvLabel: "Target hours",
    group: "Billable target",
    kind: "hours",
    help: TEAM_HELP.targetHours,
  },
  {
    key: "targetPct",
    label: "%",
    csvLabel: "Target %",
    group: "Billable target",
    kind: "pct",
    help: TEAM_HELP.targetPct,
  },
  {
    key: "billable",
    label: "Billable",
    csvLabel: "Billable logged",
    group: "Logged to date",
    kind: "hours",
    help: TEAM_HELP.billable,
    drill: "billable",
  },
  {
    key: "billablePct",
    label: "Billable %",
    csvLabel: "Billable %",
    group: "Logged to date",
    kind: "pct",
    help: TEAM_HELP.billablePct,
  },
  {
    key: "logged",
    label: "Total",
    csvLabel: "Total logged",
    group: "Logged to date",
    kind: "hours",
    help: TEAM_HELP.logged,
    drill: "entries",
  },
  {
    key: "utilization",
    label: "Utilization",
    csvLabel: "Utilization",
    group: "Logged to date",
    kind: "pct",
    help: TEAM_HELP.utilization,
  },
];

export function formatDetail(
  column: DetailColumn,
  values: MetricValues & { started: boolean },
): string {
  if (column.group === "Logged to date" && !values.started) return "—";
  const value = values[column.key];
  return column.kind === "pct" ? formatPct(value) : formatHours(value);
}

export const DEFAULT_DETAILS_SORT = "billablePct";

/** Keeps people carrying the chosen tag; "" keeps everyone. Runs before the table and Export. */
export function filterByTag(people: PersonMetrics[], tag: TeamTagFilter): PersonMetrics[] {
  if (tag === "below-target") return people.filter((p) => p.belowTarget);
  if (tag === "under-planned") return people.filter((p) => p.underPlanned);
  return people;
}

/** Ties (and the attention order's equals) fall back to the name. */
export const byName = (a: PersonMetrics, b: PersonMetrics) =>
  a.personName.localeCompare(b.personName);

/**
 * A number as the table sorts and filters it: blank where the cell shows "—" (the logged group
 * before the month starts), percents in whole percents so a min/max reads like the cell.
 */
function tableValue(column: DetailColumn, p: PersonMetrics): number | null {
  if (column.group === "Logged to date" && !p.started) return null;
  const value = p[column.key];
  if (value === null) return null;
  return column.kind === "pct" ? value * 100 : value;
}

function numberColumn(column: DetailColumn, label = column.label): LocalDataColumn<PersonMetrics> {
  return {
    key: column.key,
    label,
    type: "number",
    accessor: (p) => tableValue(column, p),
    help: column.help,
  };
}

const PERSON_COLUMN: LocalDataColumn<PersonMetrics> = {
  key: "name",
  label: "Person",
  type: "text",
  accessor: (p) => p.personName,
  help: TEAM_HELP.name,
};

function detail(key: DetailKey): DetailColumn {
  const found = DETAIL_COLUMNS.find((c) => c.key === key);
  if (!found) throw new Error(`no detail column ${key}`);
  return found;
}

/** The Details table: Person, then the twelve numbers. */
export const DETAILS_TABLE_COLUMNS: LocalDataColumn<PersonMetrics>[] = [
  PERSON_COLUMN,
  ...DETAIL_COLUMNS.map((c) => ({ ...numberColumn(c), group: c.group })),
];

/** The Summary table. Guidance sorts and filters by percent of available, as it is tagged. */
export const SUMMARY_TABLE_COLUMNS: LocalDataColumn<PersonMetrics>[] = [
  PERSON_COLUMN,
  numberColumn(detail("available")),
  // The bar columns read left to right, so their headers sit left too.
  {
    ...numberColumn(detail("plannedPct"), "Guidance"),
    align: "left",
    help: TEAM_SUMMARY_HELP.plannedPct,
  },
  numberColumn(detail("utilization")),
  {
    ...numberColumn(detail("billablePct"), "Billable vs target"),
    align: "left",
    help: TEAM_SUMMARY_HELP.billablePct,
  },
];
